import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  InformationalScreen,
  LoadingModal,
  Modal,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { type FormikHelpers, useFormik } from 'formik';

import {
  Footer,
  FormContainer,
  InputPassword,
  MainContainer,
  PasswordInputValidator,
  TopHeaderBackArrow,
} from '~/components/utility';
import { usePasswordValidation } from '~/hooks';
import { type RootState } from '~/reducers';
import { setupPassword } from '~/services/verify';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import { cleanPhoneNumber } from '~/utilities/cleanPhoneNumber';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';

type MyFormValues = {
  newPassword: string;
  repeatNewPassword: string;
};

const initialValues: MyFormValues = {
  newPassword: '',
  repeatNewPassword: '',
};

const mapStateToProps = ({
  env: { deviceId, osType, osVersion, appVersion },
  config: { validation },
  auth: { include },
  nav: { query },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  validation,
  patient: include?.patient,
  query,
  phone: cleanPhoneNumber(query.phone),
  isReset: query.reset === 'true',
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'password-setup';

const PasswordSetup = ({
  validation,
  deviceId,
  osType,
  osVersion,
  appVersion,
  patient,
  phone,
  isReset,
  query,
}: Props) => {
  const [meetsPasswordRequirements, setMeetsPasswordRequirements] =
    useState(false);

  const onSubmit = useCallback(
    async (
      values: MyFormValues,
      { setFieldError }: FormikHelpers<MyFormValues>,
    ) => {
      try {
        if (isReset) {
          Modal.show({
            dismissOnBackButtonPress: false,
            testID: buildTestId({
              testID,
              suffix: ['modal', 'password', 'setup', 'todo'],
            }),
            header: 'Not yet implemented!',
            details: 'The password reset API is not yet implemented.',
            primaryAction: {
              label: i18n.t('Global.microcopy.common.okCap'),
              onPress: () => {
                mediator.publish('router:redirect', {
                  url: '/login',
                  query: { ...query, phone },
                });
              },
            },
          });
        }

        const { status } = await setupPassword({
          newPassword: values.newPassword,
          osType,
          osVersion,
          appVersion,
          deviceId,
        });

        notifyApp('adc-webview:setup-password', { status, isReset });

        Modal.show({
          dismissOnBackButtonPress: false,
          details: i18n.t(
            'SignIn.content.signInForm.modals.accountCreated.message',
          ),
          testID: buildTestId({
            testID,
            suffix: ['modal', 'account', 'created'],
          }),
          primaryAction: {
            label: i18n.t(
              'SignIn.content.signInForm.modals.accountCreated.okCap',
            ),
          },
        });

        mediator.publish('router:redirect', {
          url: '/login',
          query: { ...query, phone },
        });
      } catch (err) {
        const error = AxiosError.from(err);

        notifyApp('adc-webview:setup-password', {
          status: error.status,
          error,
        });
        setFieldError(
          'newPassword',
          i18n.t(
            'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.invalid',
          ),
        );
      }
    },
    [appVersion, deviceId, osType, osVersion, query, phone, isReset],
  );

  const handleNavigateBack = useCallback(() => {
    mediator.publish('router:back');
  }, []);

  const validationSchema = usePasswordValidation({
    validateCurrentPassword: false,
    hasNewPassword: true,
    patient,
    translations: {
      newPassword: {
        required: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.required',
        ),
        minLength: () => '',
        // PasswordInputValidator handles this error message
        // if needed, uncomment the following lines to return the error message
        // minLength: (values) =>
        //   i18n.t(
        //     'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.min',
        //     values
        //   ),
        maxLength: (values) =>
          i18n.t(
            'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.max',
            values,
          ),
        trim: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.trim',
        ),
        repetitiveSequence: (values) =>
          i18n.t(
            'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.repetitiveSequence',
            values,
          ),
        context: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.context',
        ),
        dictionary: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.dictionary',
        ),
        repeatRequired: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.errors.required',
        ),
        mustMatch: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.errors.passwordMustMatch',
        ),
      },
    },
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnBlur: true,
  });

  const isFormSubmittable = isValid && meetsPasswordRequirements;

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}

      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        topNavigationProps={{
          animatedTitle: true,
          title: isReset
            ? i18n.t('PasswordSetup.titleReset')
            : i18n.t('PasswordSetup.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="nav-back" />,
            label: 'Go Back',
            onPress: handleNavigateBack,
          },
          testID: 'nav-back',
        }}
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('PasswordSetup.subtitle')}</Text>
            <FormContainer hasHiddenUserInput>
              <PasswordInputValidator
                fieldName="newPassword"
                placeholder={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.newPassword.placeholder',
                )}
                label={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.newPassword.label',
                )}
                onChangeText={handleChange('newPassword')}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur('newPassword')}
                password={values.newPassword}
                onValidate={setMeetsPasswordRequirements}
                identities={validation?.identity}
                testID={buildTestId({
                  testID,
                  suffix: ['new', 'password', 'input'],
                })}
              />

              <InputPassword
                label={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.label',
                )}
                placeholder={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.placeholder',
                )}
                testID={buildTestId({
                  testID,
                  suffix: ['repeat', 'password', 'input'],
                })}
                onBlur={handleBlur('repeatNewPassword')}
                onChangeText={handleChange('repeatNewPassword')}
                onSubmitEditing={() => {
                  if (isFormSubmittable) {
                    handleSubmit();
                  }
                }}
                defaultValue={values.repeatNewPassword}
                isInvalid={Boolean(
                  errors.repeatNewPassword && touched.repeatNewPassword,
                )}
                errorMessage={errors.repeatNewPassword}
              />
            </FormContainer>
          </InformationalScreen.Content>

          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'PasswordSetup.content.footerCreateAccount.submit.label',
              )}
              onButtonPress={handleSubmit}
              buttonTestID={buildTestId({
                testID,
                suffix: ['submit', 'button'],
              })}
              disabled={!isFormSubmittable}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(PasswordSetup);
